"use client";
import React, { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { ProductCardGrid } from './ProductCardGrid/ProductCardGrid';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { HomepageData } from '@/src/services/homepageService';
import { COUNTRY_CHANGE_EVENT, getCountryCookie } from '@/src/helpers/countryCookie';
import { useCardsService } from '@/src/hooks/useCardsService';
import styles from './HomePageClient.module.css';

const PaymentMethodsGrid = dynamic(() => import('./PaymentMethodsGrid'));
const CategoriesSection = dynamic(() => import('./CategoriesSection/CategoriesSection').then(m => ({ default: m.CategoriesSection })));

interface HomePageClientProps {
  data: HomepageData;
  // Server-rendered slots from SharedHomePage - these sections ship no client JS.
  reviewsSection?: React.ReactNode;
  blogSection?: React.ReactNode;
  benefitsSection?: React.ReactNode;
  whySection?: React.ReactNode;
  faqSection?: React.ReactNode;
  staticInfoSection?: React.ReactNode;
}

export function HomePageClient({
  data,
  reviewsSection,
  blogSection,
  benefitsSection,
  whySection,
  faqSection,
  staticInfoSection,
}: Readonly<HomePageClientProps>) {

  const t = useTranslations('homepage.sections');
  const tButtons = useTranslations('common.buttons');
  const tCrypto = useTranslations('homepage.crypto');
  const { localizeFullPath } = useLocalizedPath();
  const { getCards } = useCardsService();
  const [liveData, setLiveData] = useState<HomepageData | null>(null);

  useEffect(() => {
    const fetchFiltered = async (country: string | null) => {
      if (!country) {
        setLiveData(null);
        return;
      }
      const cards = await getCards({ limit: 36, country });
      if (cards?.items) {
        setLiveData({
          bestSellingCards: cards.items.slice(0, 12),
          allCards: cards.items.slice(12, 24),
          discountCards: cards.items.slice(18, 30),
          carouselCards: cards.items.slice(0, 12),
        });
      }
    };

    const handleChange = (e: Event) => {
      fetchFiltered((e as CustomEvent).detail);
    };
    window.addEventListener(COUNTRY_CHANGE_EVENT, handleChange);

    // The SSR HTML is country-agnostic (it must stay cacheable, ACEB-155) -
    // returning visitors with a country cookie get their filtered grid
    // overlaid here on mount, same path as a live country change.
    const initialCountry = getCountryCookie();
    if (initialCountry) {
      fetchFiltered(initialCountry);
    }

    return () => window.removeEventListener(COUNTRY_CHANGE_EVENT, handleChange);
  }, [getCards]);

  const displayData = liveData || data;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('bestSelling')}</h2>
        <Link href={localizeFullPath('/giftcards')} className={styles.viewAllButton} prefetch={false}>
          {tButtons('viewAll')}
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="m12 4-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
        </Link>
      </div>
      <div className={styles.cardSection}>
        <ProductCardGrid cards={displayData.bestSellingCards || []} eagerLoadCount={4} preloadEagerImages={false} />
      </div>

      <section className={styles.cryptoSection}>
        <header className={styles.cryptoHeading}>
          <h2 className={styles.cryptoTitle}>{tCrypto('title')}</h2>
          <p className={styles.cryptoSubtitle}>{tCrypto('description')}</p>
        </header>
        <PaymentMethodsGrid />
      </section>

      {reviewsSection}

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('allGiftCards')}</h2>
      </div>
      <div className={styles.cardSection}>
        <ProductCardGrid cards={displayData.allCards || []} />
      </div>

      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('browseCategories')}</h2>
        <Link href={localizeFullPath('/giftcards')} className={styles.viewAllButton} prefetch={false}>
          {tButtons('viewAllGiftCards')}
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="m12 4-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
        </Link>
      </div>
      <CategoriesSection />

      {blogSection}

      {benefitsSection}

      {whySection}

      {faqSection}

      {staticInfoSection}
    </div>
  );
}
